import { defineStore } from 'pinia'
import { PORTAL_ROOT } from '~/utils/portalRoutes'
import { toError } from '~/utils/http/api-error'
import {
  SubscriptionStatus
} from '~/types/account'
import type {
  ContactInfo,
  ExpiringOverview,
  ItemHistoryDemoCustomerResponse,
  ItemHistoryDemoResponse,
  ItemHistoryResponse,
  LoyaltyDashboard,
  OrderDemoHistoryResponse,
  OrderHistoryResponse,
  Address,
  Demonstrator,
  DemonstratorPhoto,
  DemonstratorSubscription,
  RecurlyPaymentInformation,
  RecurlyPaymentCreate,
  RecurlyPaymentUpdate,
  PaymentInformation,
  RecurlyPaymentCreateResponse,
  User
} from '~/types/account'
import type { DemoPaymentInformation, TableQueryPayload } from '~/types/demo'
import { filterTableUrlParams, processQueryFilter } from '~/utils'
import { defaultDemoSubscriptionFirst } from '~/utils/account'
import type { ShippingMethod } from '~/types/checkout'
import type { AccountCreateRequest, DemoAuthResponse, PasswordResetRequest } from '~/types/auth'
import { usePersistentStore } from '~/stores/persistent'
import { useLocaleStore } from '~/stores/locale'

import { useRootStore } from '~/stores/root'
import { pushSignup } from '~/utils/gtm'
import { useTypedRuntimeConfig } from '~/utils/runtimeConfig'

export const useAccountStore = defineStore('account', {
  state: () => ({
    backTo: {
      route: '',
      navigate: false
    },
    shippingMethods: [] as ShippingMethod[],
    demoOrderItems: { items: [], totalPages: 0, totalResults: 0, orderTypes: [] } as ItemHistoryDemoResponse,
    demoCustomerOrderItems: { items: [], totalPages: 0, totalResults: 0 } as ItemHistoryDemoCustomerResponse,
    orderItems: { items: [], totalPages: 0, totalResults: 0 } as ItemHistoryResponse,
    demoOrders: { orders: [], totalPages: 0, totalResults: 0, highestPurchaseAmount: 0, orderStatuses: [], orderTypes: [] } as OrderDemoHistoryResponse,
    demoCustomerOrders: { orders: [], totalPages: 0, totalResults: 0, highestPurchaseAmount: 0, orderStatuses: [], orderTypes: [] } as OrderDemoHistoryResponse,
    loyaltyDashboard: {} as LoyaltyDashboard,
    orders: { orders: [], totalPages: 0, totalResults: 0 } as OrderHistoryResponse,
    addresses: [] as Address[],
    payments: [] as RecurlyPaymentInformation[],
    demonstrators: [] as Demonstrator[],
    demonstratorPhotoCache: {
      photos: [] as DemonstratorPhoto[]
    },
    optedInSubscriptions: [] as DemonstratorSubscription[],
    optedOutSubscriptions: [] as DemonstratorSubscription[],
    skipSubscriptionDialog: false,
    // Demo identity handed to /help/account-linking after a demo signs in with
    // an unlinked account. It used to travel as route params, but vue-router 4
    // drops params the target route's path doesn't declare, so all three values
    // arrived undefined (blank name in step 1, `demoToken: undefined` posted to
    // account/link-demo). A query string would work but would put a live demo
    // token in the URL, so the payload is stashed here for the one hop instead.
    pendingDemoLink: null as DemoAuthResponse | null
  }),
  getters: {
    addressById: (state) => {
      return (addressId: string) => state.addresses.find(address => address.id === addressId) as Address
    },
    defaultAddress: (state) => {
      return state.addresses.find(address => address.defaultAddress) as Address
    },
    defaultMailingAddress: (state) => {
      return state.addresses.find(address => address.defaultMailingAddress) as Address
    },
    defaultDemonstrator: (state) => {
      return state.demonstrators.find(demonstrator => demonstrator.defaultDemonstrator) as Demonstrator
    },
    recentDemonstrators: (state) => {
      return state.demonstrators.filter(demonstrator => !demonstrator.defaultDemonstrator)
    },
    hasPayments: (state) => {
      return state.payments.length > 0
    },
    defaultPayment: (state) => {
      return state.payments.find(payment => payment.isDefaultPayment) as RecurlyPaymentInformation
    },
    paymentById: (state) => {
      return (paymentId: string) => state.payments.find(payments => payments.billingInfoId === paymentId) as RecurlyPaymentInformation
    },
    paymentList: (state) => {
      return state.payments
    },
    maxCardLimit: (state) => {
      const config = useTypedRuntimeConfig()
      return state.payments.length >= Number(config.public.MAX_SAVED_CARDS ?? 20)
    },
    filteredOptedInSubscriptions: (state) => {
      return state.optedInSubscriptions.filter(d => d.demonstrator !== undefined)
    },
    filteredOptedOutSubscriptions: (state) => {
      return state.optedOutSubscriptions.filter(d => d.demonstrator !== undefined)
    }
  },
  actions: {
    // currentPassword: see useCurrentPasswordOnEmailChange (AB#65190)
    async updateContactInfo (contactInfo: ContactInfo, currentPassword?: string) {
      await $api()('account/contact', {
        body: {
          newEmail: contactInfo.email,
          ...contactInfo,
          ...(currentPassword ? { currentPassword } : {})
        },
        method: 'PUT'
      })
    },
    async clearNotifications () {
      await $api()('account/demo/notifications/clear', { method: 'PUT' })
    },
    async changeAccountView () {
      const { $appInsights } = useNuxtApp()
      const auth = useSAuth()
      const route = useRoute()
      const router = useRouter()
      const rootStore = useRootStore()
      const persistentStore = usePersistentStore()

      try {
        rootStore.rootLoading = true
        const changeAccountToDemonstrator = !auth.isDemo.value

        // 1. Get new token to switch account
        const { bearerToken } = await $api()<{bearerToken: string}>(
          `account/get-${changeAccountToDemonstrator ? 'demo' : 'customer'}-token`,
          { method: 'POST' }
        )

        if (!bearerToken) {
          throw new Error(`Switch to ${changeAccountToDemonstrator ? 'demonstrator' : 'customer'} didn't return bearerToken`)
        }

        // 2. Sign in to get cookie expiration
        const { cookieExpiration } = await $api()<{cookieExpiration: number}>(
          `account/sign-in?authToken=${bearerToken}`,
          { method: 'POST', body: {} }
        )

        // 3. Update auth state and set cookie expiration
        await auth.fetchSession()
        await persistentStore.updateValue('expirationDialogTime', cookieExpiration, new Date(cookieExpiration * 1000))

        // 4. calling this.$reset only clears the account store
        this.clearStoreState()

        // 5. Handle navigation
        const homepage = isHomepage(route.name)

        if (changeAccountToDemonstrator) {
          await router.push(PORTAL_ROOT)
        } else if (!homepage) {
          await router.push({
            path: '/',
            query: { invitation: route.query?.invitation }
          })
        }
        await rootStore.fetchCategories()
      } catch (error) {
        $appInsights.trackException({ exception: toError(error) })
        throw error
      } finally {
        rootStore.rootLoading = false
      }
    },
    clearStoreState () {
      // Reset all state to initial values
      this.$reset()
    },
    async createAccount (accountCreateRequest: AccountCreateRequest) {
      const persistentStore = usePersistentStore()
      const utm = persistentStore.getPersistentValue('utm')
      if (utm) {
        accountCreateRequest = { ...accountCreateRequest, utmSource: utm.utmSource, utmMedium: utm.utmMedium, utmCampaign: utm.utmCampaign }
      }
      const response = await $api()<{redirectUrl?: string}>('account', { body: accountCreateRequest, method: 'POST' })

      // Add GTM tracking for signup
      const {proxy:gtm} = useScriptGoogleTagManager()
      pushSignup(gtm, {
        firstName: accountCreateRequest.customer.firstName,
        lastName: accountCreateRequest.customer.lastName,
        email: accountCreateRequest.customer.email
      } as User)

      return response
    },
    async updateDemoSubscriptionOptIn (demoId: number) {
      await $api()(`marketing/demosub/opt-in/${demoId}`, { method: 'POST' })
      await this.fetchDemoSubscriptions()
    },
    async updateDemoSubscriptionOptOut (demoId: number) {
      await $api()(`marketing/demosub/opt-out/${demoId}`, { method: 'POST' })
      const optedInSubscriptions = this.$state.optedInSubscriptions.filter(o => o.demoId !== demoId)
      const optedOutSubscription = this.$state.optedInSubscriptions.find(s => s.demoId === demoId) as DemonstratorSubscription

      this.$state.optedInSubscriptions = optedInSubscriptions
      optedOutSubscription.subscriptionStatus = SubscriptionStatus.OptedOut
      this.$state.optedOutSubscriptions = defaultDemoSubscriptionFirst([...this.$state.optedOutSubscriptions, optedOutSubscription])
    },
    async fetchNewsletterSubscription ({ email, disable } : { email: string, disable?: boolean }) {
      const auth = useSAuth()
      if (auth.isCustomer.value) {
        await $api()(`account/receive-communications?enable=${disable ? 'false' : 'true'}`, { method: 'PUT' })
        await auth.fetchSession()
      }
      if (email) {
        return await $api()('marketing/newsletter', { method: 'POST', query: { email } })
      }
    },
    async fetchDemoSubscriptions () {
      // TODO: Way too much logic in this function, needs to be moved to the B/E
      const response : { optedInDemos: DemonstratorSubscription[], optedOutDemos: DemonstratorSubscription[] } = await $api()('marketing/demosub/demos')
      const subscriptions = [...response.optedInDemos, ...response.optedOutDemos]
      const params = subscriptions.map(d => 'demoId=' + d.demoId).join('&')
      const demonstrators = await $api()<Demonstrator[]>(`demonstrator/profiles?${params}`)

      for (const subscription of subscriptions) {
        const demonstrator = demonstrators.find(d => d.demoId === subscription.demoId)
        if (demonstrator) {
          subscription.demonstrator = demonstrator
          subscription.defaultDemonstrator = this.defaultDemonstrator?.demoId === subscription.demoId
        }
      }
      const demoSubscriptions = defaultDemoSubscriptionFirst(subscriptions)
      this.$state.optedInSubscriptions = demoSubscriptions.filter(d => d.subscriptionStatus === SubscriptionStatus.OptedIn)
      this.$state.optedOutSubscriptions = demoSubscriptions.filter(d => d.subscriptionStatus === SubscriptionStatus.OptedOut)
      return response.optedInDemos
    },
    async fetchDemo (demoId: number) {
      return await $api()<Demonstrator>(`demonstrator/${demoId}`)
    },
    async fetchDemoPhoto (demonstrator: Demonstrator) {
      const demoPhoto = this.$state.demonstratorPhotoCache.photos.find(d => d.demoId === demonstrator.demoId)
      if (demoPhoto) {
        return demoPhoto.photo
      }
      const resp = await $api()<{ profileImg: string }>(`demonstrator/photo/${demonstrator.demoId}`)
      this.$state.demonstratorPhotoCache.photos.push({ demoId: demonstrator.demoId, photo: resp?.profileImg })
      return resp?.profileImg
    },
    async fetchDemoSocialMedia (demonstrator: Demonstrator) {
      return await $api()<Demonstrator['socialMedia']>(`demonstrator/social/${demonstrator.demoId}`)
    },
    async fetchDemosByHistory () {
      const demonstrators = await $api()<Demonstrator[]>('account/demos/history')
      this.$state.demonstrators = demonstrators
    },
    async updateDemo (demoId: number) {
      const demonstrator = await $api()<Demonstrator>(`account/demos/${demoId}`, { method: 'PUT' })
      if (this.$state.demonstrators) {
        const demonstratorIndex = this.$state.demonstrators.findIndex(d => d.demoId === demonstrator.demoId)
        this.$state.demonstrators.forEach((d) => { d.defaultDemonstrator = false })
        if (demonstratorIndex > -1) {
          this.$state.demonstrators.splice(demonstratorIndex, 1, demonstrator)
        } else {
          this.$state.demonstrators.push(demonstrator)
        }
      }
    },
    async fetchOrderItems (query: TableQueryPayload) {
      const params = filterTableUrlParams(query)
      const data = await $api()<ItemHistoryResponse>('account/orders/items', { query: { ...params } })
      this.$state.orderItems = data
    },
    async fetchDemoOrderItems (query: TableQueryPayload) {
      const params = filterTableUrlParams(processQueryFilter(query))
      const data = await $api()<ItemHistoryDemoResponse>('account/demo/orders/items', { query: { ...params } })
      this.$state.demoOrderItems = data
    },
    async fetchDemoCustomerOrderItems (query: TableQueryPayload) {
      const params = filterTableUrlParams(processQueryFilter(query))
      const data = await $api()<ItemHistoryDemoCustomerResponse>('account/demo/orders/customers/items', { query: { ...params } })
      this.$state.demoCustomerOrderItems = data
    },
    async fetchDemoOrders (query: TableQueryPayload) {
      const params = filterTableUrlParams(processQueryFilter(query))
      const data = await $api()<OrderDemoHistoryResponse>('/account/demo/orders/filter', { query: { ...params } })
      this.$state.demoOrders = data
    },
    async fetchShippingMethods () {
      const data = await $api()<ShippingMethod[]>('/shipping')
      this.$state.shippingMethods = data
    },
    async fetchDemoCustomerOrders (query: TableQueryPayload) {
      const params = filterTableUrlParams(processQueryFilter(query))
      const data = await $api()<OrderDemoHistoryResponse>('/account/demo/orders/customers/filter', { query: { ...params } })
      this.$state.demoCustomerOrders = data
    },
    async fetchOrders (query: TableQueryPayload) {
      const params = filterTableUrlParams(processQueryFilter(query))
      const data = await $api()<OrderHistoryResponse>('/account/orders', { query: { ...params } })
      this.$state.orders = data
    },
    async fetchLoyaltyDashboard (query: TableQueryPayload) {
      const params = filterTableUrlParams(query)
      const data = await $api()<LoyaltyDashboard>('/loyalty/dashboard', { query: { ...params } })
      this.$state.loyaltyDashboard = { ...data }
    },
    async fetchLoyaltyInventory () {
      const data = await $api()<{ currentBalance: number, formattedCurrentBalance: string, expiringBalance: ExpiringOverview}>('loyalty/inventory')
      this.$state.loyaltyDashboard.balance = { currentBalance: data.currentBalance, formattedCurrentBalance: data.formattedCurrentBalance }
      this.$state.loyaltyDashboard.expiring = data.expiringBalance
    },
    async fetchAddresses () {
      const data = await $api()<Address[]>('address')
      this.$state.addresses = data
    },
    async createAddress (address: Address) {
      const data = await $api()<{ savedAddressId: string, savedAddresses: Address[]}>('address', { body: address, method: 'POST' })
      this.$state.addresses = data.savedAddresses
      return data.savedAddresses.find((address: Address) => address.id === data.savedAddressId)
    },
    async updateAddress (address: Address) {
      const data = await $api()<{ savedAddressId: string, savedAddresses: Address[]}>('address', { body: address, method: 'PUT' })
      this.$state.addresses = data.savedAddresses
      return data.savedAddresses.find((address: Address) => address.id === data.savedAddressId)
    },
    async deleteAddress (address: string | Address) {
      const addressId = typeof address === 'string' ? address : address.id
      const data = await $api()<{ savedAddresses: Address[]}>(`address/${addressId}`, { method: 'DELETE' })
      this.$state.addresses = data.savedAddresses
    },
    async updatePassword (passwordResetRequest: PasswordResetRequest) {
      const tokenParam = passwordResetRequest.token ? `?token=${passwordResetRequest.token}` : ''
      const url = `account/password${tokenParam}`
      await $api()(url, { body: passwordResetRequest, method: 'POST' })
    },
    async createPaymentMethod (payment: RecurlyPaymentCreate) {
      const { $paymentService } = useNuxtApp()
      const rootStore = useRootStore()
      const recaptchaToken = rootStore.recaptchaToken
      const recaptchaAction = rootStore.recaptchaAction
      // token/action are single-use; clear before the call so a consumed token can't be re-sent
      rootStore.recaptchaToken = undefined
      rootStore.recaptchaAction = undefined
      const savedPayment = await $paymentService.createPaymentMethod(payment, recaptchaToken, recaptchaAction) as RecurlyPaymentCreateResponse
      const paymentIndex = this.$state.payments.findIndex(p => p.billingInfoId === savedPayment.billingInfoId)
      if (paymentIndex > -1) {
        this.$state.payments.splice(paymentIndex, 1, savedPayment)
      } else {
        this.$state.payments.push(savedPayment)
      }
      return savedPayment
    },
    async updatePaymentMethod (payment: RecurlyPaymentUpdate) {
      const { $paymentService } = useNuxtApp()
      const localeStore = useLocaleStore()
      const savedPayment = await $paymentService.updatePaymentMethod(payment, localeStore.country()?.countryCode)
      const paymentIndex = this.$state.payments.findIndex(p => p.billingInfoId === payment.billingInfoId)
      if (paymentIndex > -1) {
        this.$state.payments.splice(paymentIndex, 1, savedPayment)
      } else {
        this.$state.payments.push(savedPayment)
      }
    },
    async deletePaymentMethod (payment: PaymentInformation | DemoPaymentInformation) {
      const { $paymentService } = useNuxtApp()
      await $paymentService.deletePaymentMethod(payment)
    },
    async fetchPaymentList () {
      const data = await $api()<RecurlyPaymentInformation[]>('payment/recurly')
      this.$state.payments = data
    },
    async updateDefaultCulture (culture: string) {
      await $api()(`account/switch-default-culture/${culture}`, { method: 'PUT' })
    },
    setPendingDemoLink (demoLink: DemoAuthResponse | null) {
      // `this.$state.<field>` is this store's house style (20 other sites).
      this.$state.pendingDemoLink = demoLink
    },
    async linkDemoToCustomer ({ demoToken, password, username }: { demoToken: string, password: string, username: string }) {
      return await $api()('account/link-demo', { body: { demoToken, password, username }, method: 'POST' })
    },
    async signUpNewsletter (email: string) {
      await $api()('marketing/customer-sign-up', {
        method: 'POST',
        body: {
          email
        }
      })
    }
  }
})
